import apiClient from './client';

// ─── Shop-Verwaltung ─────────────────────────────────────────────────────────
//
// Gegenstück zu /api/shop auf der Betriebs-API. Die Preise der Tarife selbst
// stehen nicht hier, sondern bei den Tarifen – ein Preis gehört zum Tarif,
// nicht zum Verkaufsvorgang.

export interface ShopEinstellungen {
  registrierungOffen: boolean;
  registrierungHinweis: string;
  aktionProzent: number;
  aktionTitel: string;
  /** Letzter Tag der Aktion, `YYYY-MM-DD`. Leer = ohne Ende. */
  aktionBis: string;
}

export async function fetchShopEinstellungen(): Promise<ShopEinstellungen> {
  const { data } = await apiClient.get<ShopEinstellungen>('/shop/einstellungen');
  return data;
}

export async function setzeShopEinstellungen(
  aenderung: Partial<ShopEinstellungen>,
): Promise<ShopEinstellungen> {
  const { data } = await apiClient.patch<ShopEinstellungen>('/shop/einstellungen', aenderung);
  return data;
}

// ─── Zahlen ──────────────────────────────────────────────────────────────────

export interface ShopZahlen {
  bestellungen: { offen: number; freigeschaltet: number; abgebrochen: number };
  umsatzCents: number;
  offenCents: number;
  neueKonten: { woche: string; anzahl: number }[];
  jeTarif: { name: string; anzahl: number }[];
  gutscheine: { aktiv: number; eingeloest: number };
}

export async function fetchShopZahlen(): Promise<ShopZahlen> {
  const { data } = await apiClient.get<ShopZahlen>('/shop/zahlen');
  return data;
}

// ─── Bestellungen ────────────────────────────────────────────────────────────

export type BestellStatus = 'offen' | 'freigeschaltet' | 'abgebrochen';

export interface Bestellung {
  id: string;
  status: BestellStatus;
  listenpreisCents: number;
  rabattCents: number;
  betragCents: number;
  currency: string;
  rabattGrund: string | null;
  notiz: string | null;
  createdAt: string;
  bearbeitetAm: string | null;
  bearbeitetVon: string | null;
  user: { id: string; email: string } | null;
  plan: { name: string; interval: string | null } | null;
  coupon: { code: string } | null;
}

export async function fetchBestellungen(status?: BestellStatus): Promise<Bestellung[]> {
  const { data } = await apiClient.get<Bestellung[]>('/shop/bestellungen', {
    params: status ? { status } : undefined,
  });
  return data;
}

export async function schalteFrei(id: string, notiz?: string): Promise<{ message: string }> {
  const { data } = await apiClient.post(`/shop/bestellungen/${id}/freischalten`, { notiz });
  return data;
}

export async function brichAb(id: string, notiz?: string): Promise<{ message: string }> {
  const { data } = await apiClient.post(`/shop/bestellungen/${id}/abbrechen`, { notiz });
  return data;
}

// ─── Gutscheine ──────────────────────────────────────────────────────────────

export interface Gutschein {
  id: string;
  code: string;
  beschreibung: string | null;
  prozent: number | null;
  betragCents: number | null;
  gueltigAb: string | null;
  gueltigBis: string | null;
  maxEinloesungen: number | null;
  einloesungen: number;
  nurFuerPlanIds: string[];
  aktiv: boolean;
  createdAt: string;
  _count: { orders: number };
}

export interface GutscheinEingabe {
  code: string;
  beschreibung?: string | null;
  prozent?: number | null;
  betragCents?: number | null;
  gueltigAb?: string | null;
  gueltigBis?: string | null;
  maxEinloesungen?: number | null;
  nurFuerPlanIds?: string[];
  aktiv?: boolean;
}

export async function fetchGutscheine(): Promise<Gutschein[]> {
  const { data } = await apiClient.get<Gutschein[]>('/shop/gutscheine');
  return data;
}

export async function createGutschein(dto: GutscheinEingabe): Promise<Gutschein> {
  const { data } = await apiClient.post<Gutschein>('/shop/gutscheine', dto);
  return data;
}

export async function updateGutschein(
  id: string,
  dto: Partial<Omit<GutscheinEingabe, 'code'>>,
): Promise<Gutschein> {
  const { data } = await apiClient.patch<Gutschein>(`/shop/gutscheine/${id}`, dto);
  return data;
}

/** Löscht – oder legt still, wenn der Code schon in Bestellungen steckt. */
export async function deleteGutschein(
  id: string,
): Promise<{ message: string; stillgelegt: boolean }> {
  const { data } = await apiClient.delete(`/shop/gutscheine/${id}`);
  return data;
}
